import React from 'react';
import { cn } from '@/lib/utils';
import { SidebarProps } from '@/types/navigation';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import SidebarNav from '../navigation/SidebarNav';
import AnonymousSidebarNav from '../navigation/AnonymousSidebarNav';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const ChatSidebar: React.FC<SidebarProps> = ({ 
  open, 
  onToggle, 
  variant = 'persistent' 
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleLogoClick = () => {
    // Standardize logo behavior across app: always navigate to home
    navigate('/');
  };

  const sidebarContent = (
    <div className="flex flex-col h-full bg-background text-foreground">
      {/* Header with clickable logo */}
      <div 
        className="flex items-center justify-center h-16 px-4 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={handleLogoClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleLogoClick();
          }
        }}
      >
        <span className="text-xl font-bold text-primary">Posit</span>
      </div>
      
      {/* Navigation Content - Conditional based on auth status */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full py-4">
          {user ? <SidebarNav /> : <AnonymousSidebarNav />}
        </ScrollArea>
      </div>
    </div>
  );

  if (variant === 'overlay') {
    return (
      <Sheet open={open} onOpenChange={onToggle}>
        <SheetContent
          side="left"
          className="w-[280px] p-0 bg-background text-foreground z-50"
          onInteractOutside={(event) => {
            // Prevent closing on orientation changes or viewport issues
            const target = event.target as HTMLElement;
            if (target && target.closest('[data-orientation-change]')) {
              event.preventDefault();
            }
          }}
        >
          {sidebarContent}
        </SheetContent>
      </Sheet>
    );
  }

  // Persistent variant - always visible on desktop
  return (
    <div className="w-[280px] h-full bg-background text-foreground border-r border-border/60">
      {sidebarContent}
    </div>
  );
};

export default ChatSidebar; 